import Controller, { OnAfterNavigate, OnMount, OnSubmit } from '@philips-internal/oswald-ui-core/lib/client';
import { DynamicWizardStepProps, PanelAPI, WizardAPI } from '@philips-internal/oswald-ui-components';
import { BrokerService, HttpService, MessageService } from '@philips-internal/oswald-ui-core/lib/services';
import { NavigateEvent, SubmitEvent } from '@philips-internal/oswald-ui-core/lib/component/types';
import { isMobile, isMobileResolution } from '@philips-internal/oswald-ui-utils';
import { Inject } from '@odinjs/odin';

import BaseController from '../BaseController';
import { SUBSCRIBE_TOPIC } from '../../constants/subscribe-topic';
import TaskListItemType from '../../constants/task-list-item-type';
import { APPLICATION_ENDPOINTS } from '../../constants/application-endpoints';
import TaskItem from '../../interfaces/task-item';
import TASK_STATUS from '../../constants/task-status';
import TasksContext from '../../utils/tasks-context';
import { FragmentNames, TasksAliases } from '../../codegen';
import UserContext from '../../utils/user-context';
import { BS_TASK_LIST_BLOCK_PROFILE_EDITING, BS_TASK_LIST_CURRENT_WIZARD_STEP, BS_TASK_LIST_REQUEST_ABORTED, BS_TASK_LIST_VISIBLE_STATE, CTX_KEY_TASK_LIST_FIRST_STEP_RENDERED_NOT_DONE, CTX_KEY_TASK_LIST_IS_PROCESSING } from '../../constants/application-constants';
import { TasksSideBarCard } from '../tasks-side-bar/tasks-side-bar-controller';
import { removeEmptyProperties } from '../../utils/object-utils';

import './tasks-controller.css';
import TASK_ITEM_STATUS from '../../constants/task-item-status';

enum Expressions {
  FINISHED = 'patient.message.finished'
}

export enum DYNAMIC_STEP_IDENTIFIER {
  CONSENT_FORM = 'consent-form',
  PROFILE_UPDATE = 'profile-update',
  INSTITUTIONAL_MATERIAL = 'institutional-material',
  QUESTIONNAIRE = 'questionnaire',
  UPLOAD_FILE = 'upload-file',
  ASK_THE_PHYSICIAN = 'ask-the-physician',
  GUIDANCE_TO_THE_PATIENT = 'guidance-to-the-patient',
  SATISFACTION_SURVEY = 'satisfaction-survey',
}

@Controller({ component: TasksAliases.TASKS_LAYOUT, fragment: FragmentNames.TASKS })
class TasksController extends BaseController {

  @Inject(TasksAliases.WIZARD)
    wizardApi!: WizardAPI;

  @Inject(TasksAliases.WIZARD_PANEL)
    wizardPanelApi!: PanelAPI;

  dynamicSteps = [] as DynamicWizardStepProps[];
  doneSteps = [] as string[];

  getTaskList(sideBarCardData: TasksSideBarCard): void {
    this.context.set({ [CTX_KEY_TASK_LIST_IS_PROCESSING]: true });

    const queryParams = {
      eventType: sideBarCardData.eventType,
      nrSeqAgenda: sideBarCardData.scheduleNumber,
      nrSeqAtendimento: sideBarCardData.nrSeqAtendimento,
      patientId: UserContext.patientId,
    }

    this.wizardPanelApi.setVisible(false);
    HttpService.get(APPLICATION_ENDPOINTS.GET_TASK_LIST, removeEmptyProperties(queryParams))
      .then((res) => {
        this.doneSteps = [];
        const taskList = res.body as TaskItem[];
        const dynamicSteps = taskList.map(task => {
          const { ieStatusTarefa, ieTipoTarefa, nrSequencia, statusTaskCalculated } = task;
          const done = (ieStatusTarefa === TASK_STATUS.COMPLETED || statusTaskCalculated === TASK_ITEM_STATUS.BLOCKED);
          const step = this.getDynamicStep(ieTipoTarefa, nrSequencia, done, statusTaskCalculated);

          if (done) {
            this.doneSteps.push(step.identifier);
          }

          return step;
        });

        this.setTaskListFirstStepNotDoneOnContext(dynamicSteps);
        this.dynamicSteps = dynamicSteps;
        this.context.set({ taskList })

        const isTaskListProcessing = this.context.get(CTX_KEY_TASK_LIST_IS_PROCESSING)
        if (isTaskListProcessing) {
          this.wizardPanelApi.setVisible(true);
        }
      }).finally(() => {
        this.context.set({ [CTX_KEY_TASK_LIST_IS_PROCESSING]: false });
      })
  }

  // Adds in the context the initial step not done that will be rendered by the wizard
  private setTaskListFirstStepNotDoneOnContext(dynamicSteps: DynamicWizardStepProps[]): void {
    const firstStepNotDone = dynamicSteps.find(step => !step.props.done);
    this.context.set({ [CTX_KEY_TASK_LIST_FIRST_STEP_RENDERED_NOT_DONE]: firstStepNotDone?.identifier });
  }

  getDynamicStep(taskType: string, nrSequencia: number, done: boolean, statusTaskCalculated: string): DynamicWizardStepProps {
    const translate = (expression: string): string => {
      return statusTaskCalculated === TASK_ITEM_STATUS.BLOCKED
        ? `${MessageService.translate(expression)} (${MessageService.translate('patient.blocked')})`
        : MessageService.translate(expression);
    }

    switch(taskType) {
      case TaskListItemType.CONSENT_FORM: return {
        fragment: FragmentNames.CONSENT_FORM,
        identifier: `${DYNAMIC_STEP_IDENTIFIER.CONSENT_FORM}-${nrSequencia}`,
        props: {
          done,
          title: translate('patient.consent-form'),
        }
      }
      case TaskListItemType.PROFILE_UPDATE: return {
        fragment: FragmentNames.PROFILE_UPDATE,
        identifier: `${DYNAMIC_STEP_IDENTIFIER.PROFILE_UPDATE}-${nrSequencia}`,
        props: {
          done,
          title: translate('patient.profile-update'),
        }
      }
      case TaskListItemType.INSTITUTIONAL_MATERIAL: return {
        fragment: FragmentNames.INSTITUTIONAL_MATERIAL,
        identifier: `${DYNAMIC_STEP_IDENTIFIER.INSTITUTIONAL_MATERIAL}-${nrSequencia}`,
        props: {
          done,
          title: translate('patient.institutional-material'),
        }
      }
      case TaskListItemType.QUESTIONNAIRE: return {
        fragment: FragmentNames.QUESTIONNAIRE,
        identifier: `${DYNAMIC_STEP_IDENTIFIER.QUESTIONNAIRE}-${nrSequencia}`,
        props: {
          done,
          title: translate('patient.questionnaire'),
        }
      }
      case TaskListItemType.UPLOAD_FILE: return {
        fragment: FragmentNames.UPLOAD_FILE,
        identifier: `${DYNAMIC_STEP_IDENTIFIER.UPLOAD_FILE}-${nrSequencia}`,
        props: {
          done,
          title: translate('patient.upload-file'),
        }
      }
      case TaskListItemType.ASK_THE_PHYSICIAN: return {
        fragment: FragmentNames.ASK_THE_PHYSICIAN,
        identifier: `${DYNAMIC_STEP_IDENTIFIER.ASK_THE_PHYSICIAN}-${nrSequencia}`,
        props: {
          done,
          title: translate('patient.ask-the-doctor'),
        }
      }
      case TaskListItemType.GUIDANCE_TO_THE_PATIENT: return {
        fragment: FragmentNames.GUIDANCE_TO_THE_PATIENT,
        identifier: `${DYNAMIC_STEP_IDENTIFIER.GUIDANCE_TO_THE_PATIENT}-${nrSequencia}`,
        props: {
          done,
          title: translate('patient.guidance-to-the-patient'),
        }
      }
      case TaskListItemType.SATISFACTION_SURVEY: return {
        fragment: FragmentNames.QUESTIONNAIRE,
        identifier: `${DYNAMIC_STEP_IDENTIFIER.SATISFACTION_SURVEY}-${nrSequencia}`,
        props: {
          done,
          title: translate('patient.satisfaction-survey'),
        }
      }
      default: return {
        fragment: '',
        identifier: '',
        props: {
          title: ''
        }
      };
    }
  }

  @OnMount(TasksAliases.TASKS_LAYOUT)
  onLayoutMount(): void {
    const { nrSeqAgenda, nrAtendimento } = TasksContext;

    this.listenerTaskListAborted();
    this.listenerTaskListVisibleState();
    BrokerService.subscribeToTopic(SUBSCRIBE_TOPIC.TASKS_SIDE_BAR, (sideBarCardData: TasksSideBarCard) => {
      if (this.isTaskLoaded()) {
        this.getTaskList(sideBarCardData);
      }
    });

    if (!nrSeqAgenda && !nrAtendimento) {
      this.wizardPanelApi.setVisible(false);
    }
  }

  listenerTaskListAborted = (): void => {
    BrokerService.subscribeToTopic(BS_TASK_LIST_REQUEST_ABORTED, (isTaskListAborted: boolean) => {
      /**
       * If the task listing call is in process and this button has been clicked,
       * the value of CTX_KEY_TASK_LIST_IS_PROCESSING will be false, indicating that the task listing is not being loaded.
       */
      this.context.set({ [CTX_KEY_TASK_LIST_IS_PROCESSING]: !isTaskListAborted });
    })
  }

  listenerTaskListVisibleState = (): void => {
    BrokerService.subscribeToTopic(BS_TASK_LIST_VISIBLE_STATE, (isTaskListVisible: boolean) => {
      const isTaskListProcessing = this.context.get(CTX_KEY_TASK_LIST_IS_PROCESSING);
      if (!isTaskListProcessing && (isMobile() || isMobileResolution())) {
        this.wizardPanelApi.setVisible(!isTaskListVisible);
      }
    })
  }

  @OnMount(TasksAliases.WIZARD)
  onWizardMount(): void {
    const { wizardApi, doneSteps, dynamicSteps, wizardPanelApi } = this;

    if (dynamicSteps.length) {
      wizardApi.setDynamicSteps(dynamicSteps);

      for (const step of dynamicSteps) {
        BrokerService.subscribeToTopic(step.identifier, (done: boolean) => {
          setTimeout(() => {
            wizardApi.setStepDone(step.identifier, done);
            const hasIdentifierBeenAdded = doneSteps.some(doneStep => doneStep === step.identifier);

            if (!hasIdentifierBeenAdded) {
              doneSteps.push(step.identifier)
            }

            const cardId = TasksContext.selectedCardId;
            if (cardId && !hasIdentifierBeenAdded) {
              BrokerService.publishToTopic(cardId, doneSteps.length)
            }
          });
        });
      }
    } else {
      wizardPanelApi.setVisible(false);
    }
  }

  @OnAfterNavigate(TasksAliases.WIZARD)
  onWizardAfterNavigate({ from, to }: NavigateEvent<string>): void {
    const { doneSteps, wizardApi } = this;

    // Notify the current step
    BrokerService.publishToTopic(BS_TASK_LIST_CURRENT_WIZARD_STEP, to);

    const steps = wizardApi.getSteps();
    if (doneSteps.includes(from) && !steps[from].done) {
      wizardApi.setStepDone(from, true);
    }

    const isAllFinished = Object.keys(steps).every(stepId => steps[stepId].done)
    if (isAllFinished && wizardApi.getConfirmExpression() !== Expressions.FINISHED) {
      wizardApi.setConfirmExpression(Expressions.FINISHED);
    }

    this.triggerProfileFormBlocking(doneSteps, to);
  }

  @OnSubmit(TasksAliases.WIZARD)
  onSubmitWizard(event: SubmitEvent): void {
    const wizardApi = event.target as WizardAPI;
    wizardApi.setConfirmExpression(Expressions.FINISHED);

    // This is necessary because in mobile mode only calling wizardApi.setConfirmExpression has no effect.
    const currentStepId = wizardApi.getCurrentStepIdentifier();
    wizardApi.setStepDone(currentStepId, false);
    wizardApi.setStepDone(currentStepId, true);
  }

  private triggerProfileFormBlocking(doneSteps: string[], currentFragmentId?: string): void {
    if (currentFragmentId?.startsWith(FragmentNames.PROFILE_UPDATE) && doneSteps.includes(currentFragmentId)) {
      BrokerService.publishToTopic(BS_TASK_LIST_BLOCK_PROFILE_EDITING, true);
    }
  }

  private isTaskLoaded(): boolean {
    try {
      this.wizardPanelApi.setVisible(true);
      return this.wizardPanelApi.isVisible();
    } catch (e) {
      return false;
    }
  }
}

export default TasksController;
